import React, {useState, useRef} from 'react';
import ReactDOM from 'react-dom';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  Placement,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';

import {DisplaySize} from 'common/components/tooltip/types';
import {TooltipReact} from 'common/components/tooltip';

import styles from './new-tooltip.module.styl';

type Props = {
  content: string | React.ReactElement;
  size: DisplaySize;
  placement: Placement;
  useArrow?: boolean;
  /** Whether to show the tooltip or not. */
  enabled?: boolean;
  /** The element to add a tooltip to. */
  children?: React.ReactElement;
  /** If you want to anchor to something other than the child. NOTE: Prefer to use child though. */
  anchorRef?: HTMLElement;
};

/**
 * New Tooltip component using floating-ui.
 *
 * @param props set useArrow to true to use floating-ui arrow instead of tooltip.
 * @returns
 */
export function NewTooltip(props: Props) {
  const [showTooltip, setShowTooltip] = useState(false);
  const childRef = useRef<HTMLElement | null>(null);
  const arrowRef = useRef(null);

  const element = childRef.current || props.anchorRef;
  const enabled = (props.enabled !== undefined ? props.enabled : true) && props.content;

  const root = document.querySelector(`#tooltips`) || document.body;

  const {refs, floatingStyles, context} = useFloating({
    placement: props.placement,
    // temporarily disabling flip and arrow middleware to handle compatibility with the existing Tooltip component
    // TODO: enable flip and arrow for all tooltip instances; test use case for boards
    middleware: [
      props.useArrow &&
        flip({
          fallbackAxisSideDirection: 'start',
          crossAxis: false,
        }),
      shift(),
      props.useArrow &&
        arrow({
          element: arrowRef,
        }),
    ],
    whileElementsMounted: autoUpdate,
    elements: {reference: element},
    open: showTooltip,
    onOpenChange: setShowTooltip,
  });

  const hover = useHover(context, {
    handleClose: safePolygon(),
  });

  const {getFloatingProps} = useInteractions([hover]);

  const clonedChild = props.children
    ? React.cloneElement(props.children, {
        ref: (el: any) => {
          childRef.current = el;

          // preserve any ref that might be passed in to the child
          const originalRef = (props.children as any)?.ref;
          if (typeof originalRef === `function`) {
            originalRef(el);
          } else if (originalRef) {
            originalRef.current = el;
          }
        },
      })
    : null;

  return (
    <>
      {clonedChild}
      {showTooltip &&
        enabled &&
        ReactDOM.createPortal(
          <div
            className={styles.tooltipContainer}
            ref={refs.setFloating}
            style={{...floatingStyles}}
            {...getFloatingProps()}
          >
            {props.useArrow ? (
              <div className={styles.tooltip}>
                <FloatingArrow ref={arrowRef} context={context} fill={`var(--inverted-component-background-color)`} />
                <div className={styles.tooltipContent}>{props.content}</div>
              </div>
            ) : (
              <TooltipReact forceShow placement={props.placement as any} size={props.size}>
                {props.content}
              </TooltipReact>
            )}
          </div>,
          root,
        )}
    </>
  );
}
